/**
 * 验证码服务模块
 * 基于Authing Web SDK实现邮箱和手机验证码发送
 */

// @ts-nocheck - 服务文件，允许类型检查宽松
// import i18n from '@/i18n'; // 改为动态导入避免TDZ
import { AuthenticationClient, EmailScene } from 'authing-js-sdk';
import { getAuthingConfig } from '@/config/authing';
import {
  SendCodeRequest,
  SendCodeResponse,
  VerifyCodeRequest,
  VerifyCodeResponse,
  LoginResponse,
  RegisterResponse,
  VerificationCodeScene,
  VerificationCodeType
} from '@/types/unifiedAuth';

// 向后兼容的类型别名
export interface VerificationCodeOptions {
  email?: string;
  phone?: string;
  scene?: 'RESET_PASSWORD' | 'VERIFY_CODE' | 'UPDATE_EMAIL' | 'UPDATE_PHONE' | 'BIND_EMAIL' | 'BIND_PHONE' | 'DELETE_ACCOUNT' | 'REGISTER' | 'LOGIN';
  channel?: 'SMS' | 'EMAIL';
}

export interface VerificationCodeResponse {
  success: boolean;
  message: string;
  data?: any;
}

class VerificationCodeService {
  private authClient: AuthenticationClient | null = null;
  // 存储验证码发送时返回的token信息
  private verificationTokens: Map<string, any> = new Map();

  /**
   * 初始化Authing认证客户端
   */
  private async initAuthClient(): Promise<AuthenticationClient> {
    if (this.authClient) {
      return this.authClient;
    }

    try {
      const config = getAuthingConfig();
      
      // 🔧 FIX: 尝试不同的初始化方式来解决认证权限问题
      console.log('🔍 尝试initializationAuthingclient，configuration:', {
        appId: config.appId,
        domain: config.domain,
        host: config.host,
        appIdValid: !!config.appId,
        hostValid: !!config.host
      });
      
      if (!config.appId || !config.host) {
        throw new Error(`Authing配置缺失: appId=${!!config.appId}, host=${!!config.host}`);
      }

      // 🔧 FIX: 增加超时配置和网络优化 + 本地开发通过Vite代理避免CORS
      const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
      const appHost = isLocal ? `${window.location.origin}/api/authing` : `https://${config.domain}`;

      this.authClient = new AuthenticationClient({
        appId: config.appId,
        appHost,
        protocol: 'oidc',
        // 🔧 FIX: 统一超时时间到90秒，解决认证超时问题
        timeout: 90000,
        // 🔧 FIX: 添加网络优化配置
        requestConfig: {
          withCredentials: false,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Cache-Control': 'no-cache',
            'User-Agent': 'WenPai-App/1.0.0'
          }
        }
      });

      console.log('✅ Authing AuthenticationClientinitializationsuccess，appHost:', appHost);
      return this.authClient;
    } catch (error) {
      console.error('❌ Authing AuthenticationClientinitializationfailed:', error);
      throw new Error(i18n.t('common.errors.认证客户端初始化失败'));
    }
  }

  /**
   * 发送手机验证码
   */
  async sendSmsCode(phone: string, scene: string = 'LOGIN'): Promise<SendCodeResponse> {
    try {
      // 验证手机号格式
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的手机号码')
        };
      }

      const client = await this.initAuthClient();
      
      console.log('📱 调用SDKsendingphonevalidating码:', { phone, scene });
      
      // 调用Authing SDK发送短信验证码
      const result = await client.sendSmsCode(phone);

      console.log('✅ phonevalidating码sendingAPI返回:', { result, resultType: typeof result });
      
      return {
        success: true,
        message: i18n.t('common.messages.验证码已发送到您的手机'),
        data: result
      };

    } catch (error: any) {
      console.error('❌ sendingphonevalidating码failed:', error);
      
      let errorMessage = i18n.t('common.errors.发送验证码失败');
      
      // 根据错误类型返回具体错误信息
      if (error?.message) {
        if (error.message.includes('rate limit')) {
          errorMessage = '发送频率过快，请稍后再试';
        } else if (error.message.includes('phone')) {
          errorMessage = i18n.t('common.errors.手机号格式错误');
        } else if (error.message.includes('invalid')) {
          errorMessage = i18n.t('common.errors.无效的请求参数');
        } else {
          errorMessage = error.message;
        }
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 发送邮箱验证码
   */
  async sendEmailCode(email: string, scene: string = 'LOGIN'): Promise<SendCodeResponse> {
    try {
      // 验证邮箱格式
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的邮箱地址')
        };
      }

      const client = await this.initAuthClient();
      
      // 🔧 FIX: 尝试多种邮件发送方式来获取正确的token
      let result;
      let emailScene: EmailScene;
      
      console.log('🔍 sendingvalidating码场景:', scene);
      
      try {
        // 方法1: 标准的注册验证码
        if (scene.toUpperCase() === 'REGISTER') {
          emailScene = EmailScene.REGISTER_VERIFY_CODE;
          console.log('📧 使用registervalidating码场景');
        } else {
          emailScene = EmailScene.LOGIN_VERIFY_CODE;
          console.log('📧 使用loginvalidating码场景');
        }

        result = await client.sendEmail(email, emailScene);
        
        // 检查结果结构
        console.log('📧 sendingvalidating码API原始result:', {
          result,
          resultType: typeof result,
          resultKeys: result && typeof result === 'object' ? Object.keys(result) : 'not object'
        });

      } catch (sendError) {
        console.log('📧 标准sending方式failed，尝试备用方式:', sendError);
        
        // 方法2: 如果注册验证码失败，尝试登录验证码
        try {
          emailScene = EmailScene.LOGIN_VERIFY_CODE;
          result = await client.sendEmail(email, emailScene);
          console.log('📧 备用方式sendingsuccess');
        } catch (backupError) {
          console.log('📧 备用sending方式也failed:', backupError);
          throw sendError; // 抛出原始错误
        }
      }

      // 🔧 FIX: 保存验证码发送返回的token信息
      if (result) {
        // 保存整个result对象，因为emailToken可能在不同的层级
        this.verificationTokens.set(email, result);
        console.log('💾 savingvalidating码Token:', { email, tokenInfo: result });
      }

      console.log('✅ emailvalidating码sendingsuccess:', { email, scene, result });
      
      return {
        success: true,
        message: i18n.t('common.messages.验证码已发送到您的邮箱'),
        data: result
      };

    } catch (error: any) {
      console.error('❌ sendingemailvalidating码failed:', error);
      console.error('❌ errordetails:', {
        message: error?.message,
        code: error?.code,
        status: error?.status,
        response: error?.response?.data,
        stack: error?.stack
      });
      
      let errorMessage = i18n.t('common.errors.发送验证码失败');
      
      // 根据错误类型返回具体错误信息
      if (error?.message) {
        if (error.message.includes('rate limit')) {
          errorMessage = '发送频率过快，请稍后再试';
        } else if (error.message.includes('email')) {
          errorMessage = i18n.t('common.errors.邮箱格式错误');
        } else if (error.message.includes('invalid')) {
          errorMessage = i18n.t('common.errors.无效的请求参数');
        } else {
          errorMessage = error.message;
        }
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 验证手机验证码并登录
   */
  async loginByPhoneCode(phone: string, code: string): Promise<LoginResponse> {
    try {
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的手机号码')
        };
      }

      if (!code || code.length < 4) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的验证码')
        };
      }

      const client = await this.initAuthClient();
      
      const result = await client.loginByPhoneCode(phone, code);

      // 🔍 验证Authing API返回结果 - 简化验证条件，避免误判
      if (!result) {
        throw new Error('API返回空结果');
      }

      console.log('✅ phonevalidating码loginsuccess:', result);
      
      return {
        success: true,
        message: i18n.t('common.messages.登录成功'),
        data: result
      };

    } catch (error: any) {
      console.error('❌ phonevalidating码loginfailed:', error);
      
      let errorMessage = i18n.t('common.errors.登录失败');
      if (error?.message) {
        if (error.message.includes('code') || error.message.includes(i18n.t('common.errors.验证码'))) {
          errorMessage = i18n.t('common.errors.验证码错误或已过期');
        } else if (error.message.includes('phone')) {
          errorMessage = i18n.t('common.errors.手机号不存在或未注册');
        } else if (error.message.includes(i18n.t('common.errors.授权码')) || error.message.includes('authorization')) {
          errorMessage = '验证码无效，请重新获取';
        } else if (error.message.includes('400')) {
          errorMessage = i18n.t('common.errors.请检查手机号格式或重新获取验证码');
        } else {
          errorMessage = error.message;
        }
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 验证邮箱验证码并登录
   */
  async loginByEmailCode(email: string, code: string): Promise<LoginResponse> {
    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的邮箱地址')
        };
      }

      if (!code || code.length < 4) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的验证码')
        };
      }

      const client = await this.initAuthClient();
      
      const result = await client.loginByEmailCode(email, code);

      // 🔍 验证Authing API返回结果 - 简化验证条件，避免误判
      if (!result) {
        throw new Error('API返回空结果');
      }

      console.log('✅ emailvalidating码loginsuccess:', result);
      
      return {
        success: true,
        message: i18n.t('common.messages.登录成功'),
        data: result
      };

    } catch (error: any) {
      console.error('❌ emailvalidating码loginfailed:', error);
      
      let errorMessage = i18n.t('common.errors.登录失败');
      if (error?.message) {
        if (error.message.includes('code') || error.message.includes(i18n.t('common.errors.验证码'))) {
          errorMessage = i18n.t('common.errors.验证码错误或已过期');
        } else if (error.message.includes('email')) {
          errorMessage = i18n.t('common.errors.邮箱不存在或未注册');
        } else if (error.message.includes(i18n.t('common.errors.授权码')) || error.message.includes('authorization')) {
          errorMessage = '验证码无效，请重新获取';
        } else if (error.message.includes('400')) {
          errorMessage = i18n.t('common.errors.请检查邮箱格式或重新获取验证码');
        } else {
          errorMessage = error.message;
        }
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 使用验证码注册新用户
   */
  async registerByPhoneCode(phone: string, code: string, password: string): Promise<RegisterResponse> {
    try {
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的手机号码')
        };
      }

      if (!code || code.length < 4) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的验证码')
        };
      }

      if (!password || password.length < 6) {
        return {
          success: false,
          message: '密码长度至少6位'
        };
      }

      const client = await this.initAuthClient();
      
      const result = await client.registerByPhoneCode(phone, code, password);

      console.log('✅ phonevalidating码registersuccess:', result);
      
      return {
        success: true,
        message: i18n.t('common.messages.注册成功'),
        data: result
      };

    } catch (error: any) {
      console.error('❌ phonevalidating码registerfailed:', error);
      
      let errorMessage = i18n.t('common.errors.注册失败');
      if (error?.message) {
        if (error.message.includes('code')) {
          errorMessage = i18n.t('common.errors.验证码错误或已过期');
        } else if (error.message.includes('phone')) {
          errorMessage = i18n.t('common.errors.手机号已被注册');
        } else if (error.message.includes('password')) {
          errorMessage = i18n.t('common.errors.密码格式不符合要求');
        } else {
          errorMessage = error.message;
        }
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 检查邮箱是否已注册
   */
  async checkEmailExists(email: string): Promise<boolean> {
    try {
      const client = await this.initAuthClient();
      // 尝试使用该邮箱发送重置密码验证码来检测邮箱是否存在
      // 如果邮箱不存在，通常会返回错误
      const result = await (client as any).sendEmail(email, 'RESET_PASSWORD_VERIFY_CODE');
      return true; // 如果成功发送，说明邮箱已注册
    } catch (error: any) {
      // 如果发送失败且错误信息表明用户不存在，则邮箱未注册
      if (error?.message && (
        error.message.includes(i18n.t('common.errors.用户不存在')) || 
        error.message.includes('User does not exist') ||
        error.message.includes(i18n.t('common.errors.未找到用户'))
      )) {
        return false;
      }
      // 其他错误情况，假设邮箱已注册（保守处理）
      return true;
    }
  }

  /**
   * 使用验证码注册新用户（邮箱）
   */
  async registerByEmailCode(email: string, code: string, password: string): Promise<RegisterResponse> {
    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的邮箱地址')
        };
      }

      if (!code || code.length < 4) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的验证码')
        };
      }

      if (!password || password.length < 6) {
        return {
          success: false,
          message: '密码长度至少6位'
        };
      }

      console.log('🚀 step1: startsinitializationauthenticatingclient...');
      const client = await this.initAuthClient();
      console.log('✅ step1completed: authenticatingclientinitializationsuccess');

      // 清理验证码（去除空格和特殊字符）
      const cleanCode = code.trim().replace(/\s+/g, '');
      console.log('🔍 step2: validating码infoprocessing', {
        原始验证码: code,
        清理后验证码: cleanCode,
        验证码长度: cleanCode.length,
        邮箱: email
      });

      console.log('🚀 step3: starts调用registerByEmailCode API...');
      
      // 🔧 FIX: 获取发送验证码时保存的token
      const tokenInfo = this.verificationTokens.get(email);
      console.log('🔍 gettingsaving的Tokeninfo:', { email, tokenInfo });
      
      // 🔧 FIX: 尝试从不同的字段获取emailToken，确保获取到字符串值
      let emailToken = null;
      if (tokenInfo) {
        // 尝试多种字段名和嵌套结构
        const candidates = [
          tokenInfo.emailToken,
          tokenInfo.data?.emailToken,
          tokenInfo.token,
          tokenInfo.data?.token,
          tokenInfo.data,
          tokenInfo
        ];
        
        console.log('🔍 Token候选valuedetails:', {
          tokenInfo,
          tokenInfoStringified: JSON.stringify(tokenInfo, null, 2),
          candidates: candidates.map((c, i) => ({ 
            index: i, 
            value: c, 
            type: typeof c,
            isObject: typeof c === 'object' && c !== null,
            keys: (typeof c === 'object' && c !== null) ? Object.keys(c) : undefined,
            stringified: JSON.stringify(c)
          }))
        });
        
        // 找到第一个字符串类型的token
        for (const candidate of candidates) {
          if (typeof candidate === 'string' && candidate.length > 0) {
            emailToken = candidate;
            break;
          }
          // 如果是对象，尝试寻找内部的字符串字段
          if (candidate && typeof candidate === 'object') {
            const stringFields = ['emailToken', 'token', 'id', 'key', 'value'];
            for (const field of stringFields) {
              if (typeof candidate[field] === 'string' && candidate[field].length > 0) {
                emailToken = candidate[field];
                break;
              }
            }
            if (emailToken) break;
          }
        }
        
        console.log('🔍 最终提取的emailToken:', { 
          emailToken, 
          type: typeof emailToken,
          length: emailToken?.length,
          tokenInfoKeys: Object.keys(tokenInfo)
        });
      }
      
      if (!emailToken) {
        // 如果没有token，我们尝试不使用token的方式注册
        console.log('⚠️ not foundemailToken，尝试直接register...');
      }

      // 🔧 FIX: 使用正确的注册API调用方式
      const profile = {
        password: password,
        email: email
      };
      
      console.log('📡 API调用parameter:', { 
        email, 
        code: cleanCode, 
        emailToken: emailToken,
        profile: profile 
      });

      // 🔧 FIX: 简化API调用逻辑，避免TDZ错误
      console.log('🔧 startsemailregisterAPI调用...');
      
      // 🔧 使用立即执行的异步函数避免变量作用域问题
      const registerPromise = (async () => {
        // 方法1: 如果有有效的emailToken，尝试4参数调用
        if (emailToken && typeof emailToken === 'string' && emailToken.length > 10) {
          console.log('🔧 method1: 使用emailTokenstring (4parameter)');
          try {
            return await (client as any).registerByEmailCode(email, cleanCode, emailToken, { password });
          } catch (error) {
            console.log('🔧 method1failed，尝试method2:', error);
          }
        }
        
        // 方法2: 标准3参数调用
        console.log('🔧 method2: 标准3parameter调用 (email, code, profile)');
        try {
          return await client.registerByEmailCode(email, cleanCode, {
            email: email
          });
        } catch (error) {
          console.log('🔧 method2failed，尝试method3:', error);
        }
        
        // 方法3: 最简单的调用方式
        console.log('🔧 method3: 最简单调用 (email, code, password)');
        try {
          return await (client as any).registerByEmailCode(email, cleanCode, password);
        } catch (error) {
          console.log('🔧 method3failed:', error);
          throw new Error('所有注册方式均失败，可能是API配置问题');
        }
      })();
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('注册请求超时(30秒)')), 30000);
      });

      const result = await Promise.race([registerPromise, timeoutPromise]);
      
      // 🔧 清理使用过的token
      this.verificationTokens.delete(email);
      console.log('🗑️ cleaning使用过的validating码Token:', { email });
      console.log('✅ step3completed: registerByEmailCode调用success');

      console.log('✅ step3completed: emailvalidating码registersuccess，none需单独settingpassword:', result);
      
      return {
        success: true,
        message: i18n.t('common.messages.注册成功'),
        data: result
      };

    } catch (error: any) {
      console.error('❌ emailvalidating码registerfailed:', error);
      console.error('❌ registererrordetails:', {
        message: error?.message,
        code: error?.code,
        status: error?.status,
        response: error?.response?.data,
        data: error?.data,
        statusText: error?.statusText,
        config: error?.config?.url,
        stack: error?.stack?.split('\n')[0] // 只显示第一行堆栈
      });

      // 额外的错误信息输出
      console.error('❌ 完整errorobject:', JSON.stringify(error, null, 2));

      // 特别检查认证相关错误
      if (error?.message?.includes(i18n.t('common.errors.登录')) || error?.message?.includes(i18n.t('common.errors.权限')) || error?.message?.includes('unauthorized')) {
        console.error('🚨 authenticating相关errordetecting到！');
        console.error('🔍 currentauthenticatingstate:', {
          hasToken: !!localStorage.getItem('authing_token'),
          hasAuthClient: !!this.authClient,
          clientConfig: this.authClient ? 'initialized' : 'not initialized'
        });
      }

      let errorMessage = i18n.t('common.errors.注册失败');
      if (error?.message) {
        if (error.message.includes('code') || error.message.includes(i18n.t('common.errors.验证码'))) {
          errorMessage = '验证码错误或已过期，请重新获取验证码';
        } else if (error.message.includes('email') || error.message.includes(i18n.t('common.errors.邮箱'))) {
          // 🔧 FIX: 更详细的邮箱错误处理
          if (error.message.includes(i18n.t('common.errors.已存在')) || error.message.includes(i18n.t('common.errors.已被注册')) || error.message.includes(i18n.t('common.errors.已注册'))) {
            errorMessage = '该邮箱已被注册，请使用其他邮箱或尝试登录';
          } else if (error.message.includes('emailToken') || error.message.includes(i18n.t('common.errors.进行校验'))) {
            errorMessage = '验证码验证失败，请重新获取验证码后再试';
          } else {
            errorMessage = '邮箱验证失败，请检查邮箱格式';
          }
        } else if (error.message.includes('password')) {
          errorMessage = '密码格式不符合要求（至少6位）';
        } else if (error.message.includes(i18n.t('common.errors.权限')) || error.message.includes('unauthorized')) {
          errorMessage = '认证失败，请刷新页面重试';
        } else {
          errorMessage = error.message;
        }
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 验证邮箱验证码（用于更新邮箱）
   */
  async verifyEmailCode(email: string, code: string): Promise<VerifyCodeResponse> {
    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的邮箱地址')
        };
      }

      if (!code || code.length < 4) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的验证码')
        };
      }

      const client = await this.initAuthClient();
      
      // 调用Authing SDK验证邮箱验证码
      const result = await (client as any).verifyEmailCode(email, code);

      console.log('✅ emailvalidating码validatingsuccess:', { email });
      
      return {
        success: true,
        message: i18n.t('common.messages.邮箱验证成功'),
        data: result
      };

    } catch (error: any) {
      console.error('❌ validatingemailvalidating码failed:', error);
      
      let errorMessage = '验证失败';
      if (error?.message) {
        if (error.message.includes('code')) {
          errorMessage = i18n.t('common.errors.验证码错误或已过期');
        } else if (error.message.includes('email')) {
          errorMessage = i18n.t('common.errors.邮箱地址错误');
        } else {
          errorMessage = error.message;
        }
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 验证手机验证码（用于更新手机号）
   * 🔧 FIX: 2025-08-30 移除不存在的verifySmsCode方法，使用登录验证流程
   */
  async verifyPhoneCode(phone: string, code: string): Promise<VerifyCodeResponse> {
    try {
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的手机号码')
        };
      }

      if (!code || code.length < 4) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的验证码')
        };
      }

      // 🔧 使用登录验证码来验证手机号有效性
      // 由于Authing SDK没有独立的verifySmsCode方法，我们通过尝试登录来验证验证码
      const loginResult = await this.loginByPhoneCode(phone, code);
      
      if (loginResult.success) {
        console.log('✅ phonevalidating码validatingsuccess:', { phone });
        
        return {
          success: true,
          message: i18n.t('common.messages.手机号验证成功'),
          data: loginResult.data
        };
      } else {
        return {
          success: false,
          message: loginResult.message
        };
      }

    } catch (error: any) {
      console.error('❌ validatingphonevalidating码failed:', error);
      
      let errorMessage = '验证失败';
      if (error?.message) {
        if (error.message.includes('code')) {
          errorMessage = i18n.t('common.errors.验证码错误或已过期');
        } else if (error.message.includes('phone')) {
          errorMessage = i18n.t('common.errors.手机号格式错误');
        } else {
          errorMessage = error.message;
        }
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 使用手机验证码重置密码
   */
  async resetPasswordByPhoneCode(phone: string, code: string, newPassword: string): Promise<VerifyCodeResponse> {
    try {
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的手机号码')
        };
      }

      if (!code || code.length < 4) {
        return {
          success: false,
          message: i18n.t('common.messages.请输入有效的验证码')
        };
      }

      if (!newPassword || newPassword.length < 6) {
        return {
          success: false,
          message: '密码长度至少6位'
        };
      }

      const client = await this.initAuthClient();
      
      // Authing SDK的重置密码API
      const result = await client.resetPasswordByPhoneCode(phone, code, newPassword);

      console.log('✅ phonevalidating码resettingpasswordsuccess:', result);
      
      return {
        success: true,
        message: i18n.t('common.messages.密码重置成功'),
        data: result
      };

    } catch (error: any) {
      console.error('❌ phonevalidating码resettingpasswordfailed:', error);
      
      let errorMessage = i18n.t('common.errors.密码重置失败');
      if (error?.message) {
        if (error.message.includes('code')) {
          errorMessage = i18n.t('common.errors.验证码错误或已过期');
        } else if (error.message.includes('phone')) {
          errorMessage = i18n.t('common.errors.手机号不存在或未注册');
        } else if (error.message.includes('password')) {
          errorMessage = i18n.t('common.errors.密码格式不符合要求');
        } else {
          errorMessage = error.message;
        }
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }
}

// 导出单例实例
export const verificationCodeService = new VerificationCodeService();
export default verificationCodeService;